import {ChangeDetectionStrategy, Component, inject, Input, OnInit, ViewChild} from '@angular/core';
import {PaginationSelectors} from '@aviary/store/pagination/pagination.selectors';
import {RouterSelectors} from '@aviary/store/router/router.selectors';
import {RouterActions} from '@aviary/store/router/router.actions';
import {createSelector, Store} from '@ngrx/store';
import {kebabCaseToSentenceCase} from '@aviary/store/transformation.helpers';
import {isArray, isPopulatedArray, isUndefined} from '@aviary/store/common/typing.helpers';
import {ListFilterSelectOption, SelectOption, SelectOptions} from '@aviary/store/common/common.types';
import {TitleCasePipe} from '@aviary/pipes/title-case.pipe';
import {filter, take} from 'rxjs';
import {SelectionModel} from '@angular/cdk/collections';
import {toDateOnlyIsoString} from '@aviary/store/common/date.helpers';
import {
  FilterDefinitionType,
  FilterOptionsSelector,
  isSelectFilterDefinition,
  TableCardHeaderFilterDefinition
} from '@aviary/components/table-card/table-card-header-filters/table-card-header-filter.types';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {MatButton} from '@angular/material/button';
import {NgTemplateOutlet} from '@angular/common';
import {MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {FormatPipe} from '@aviary/pipes/format.pipe';
import {MatIcon} from '@angular/material/icon';
import {MatCheckbox} from '@angular/material/checkbox';
import {
  MatDatepickerActions,
  MatDatepickerApply,
  MatDatepickerCancel,
  MatDateRangeInput,
  MatDateRangePicker,
  MatEndDate,
  MatStartDate
} from '@angular/material/datepicker';
import {FormsModule} from '@angular/forms';
import {MxSelector} from '@aviary/store/store.types';
import {MatTooltip} from '@angular/material/tooltip';

@Component({
  selector: 'mx-table-card-header-filter',
  templateUrl: './table-card-header-filter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SelectPipe, MatButton, NgTemplateOutlet, MatMenuTrigger, FormatPipe, MatIcon, MatMenu, MatMenuContent, TitleCasePipe, MatMenuItem, MatCheckbox,
    MatDateRangeInput, FormsModule, MatDateRangePicker, MatDatepickerActions, MatDatepickerCancel, MatDatepickerApply, MatStartDate, MatEndDate, MatTooltip
  ]
})
export class TableCardHeaderFilterComponent implements OnInit {

  @ViewChild(MatDateRangePicker) set dateRangePicker(dateRangePicker: MatDateRangePicker<Date> | undefined) {
    /*
      This prevents a timing error by NOT trying to open the date range picker before it's been fully rendered.
      When this setter is invoked, AND we have a picker to open, open it after a delay.
    */
    if (!dateRangePicker || !this.showDateRangePicker) return;

    queueMicrotask(() => dateRangePicker.open());
  }

  @Input() set definition(definition: TableCardHeaderFilterDefinition) {
    this.filterType = definition.type;
    this.isMultiSelect = definition.type === 'multiSelect';
    this.queryParamName = definition.queryParamName;
    this.inactiveButtonLabel = kebabCaseToSentenceCase(definition.queryParamName);

    this.setFilterOptionsFor(definition);
  }

  public readonly routerSelectors = RouterSelectors;
  public readonly paginationSelectors = PaginationSelectors;
  public filterType: FilterDefinitionType;
  public isMultiSelect: boolean;
  public queryParamName: string;
  public inactiveButtonLabel: string;
  public filterOptionsSelector?: FilterOptionsSelector;
  public showDateRangePicker = false;

  public dateRangeStart?: Date;
  public dateRangeEnd?: Date;

  public ngOnInit(): void {
    this.initializeSelectionModel();
  }

  public onClickActiveFilterButton(): void {
    if (!this.isMultiSelect) this.removeFilter();
  }

  public onClickActiveFilterCloseIcon(event: MouseEvent): void {
    event.stopPropagation();  // Clicking this icon ALWAYS means clearing the filter.  Stop propagation so we don't also handle the containing button click.

    this.removeFilter();
    if (this.isMultiSelect) {
      this.selectionModel.clear();
      this.previousJoinedSelectionModelValue = '';
    }
  }

  public onClickDateRangeButton(): void {
    this.dateRangeStart = this.dateRangeEnd = undefined;
    this.showDateRangePicker = !this.showDateRangePicker;
  }

  public onClickDateRangeCancelButton(): void {
    this.showDateRangePicker = false;
  }

  public onClickDateRangeApplyButton(): void {
    this.showDateRangePicker = false;

    if (!this.dateRangeStart) return; // User submitted without selecting any dates -- treat this as if the user canceled the picker.

    const formattedStartDate: string = toDateOnlyIsoString(this.dateRangeStart);
    const formattedEndDate: string = this.dateRangeEnd ? toDateOnlyIsoString(this.dateRangeEnd) : formattedStartDate;

    this.addFilter(`${formattedStartDate}..${formattedEndDate}`);
  }

  public onClosedDatePicker(): void {
    this.showDateRangePicker = false;
  }

  public isSelected(option: SelectOption<string>): boolean {
    return this.selectionModel.isSelected(option.value);
  }

  public onClickMenuOption(event: MouseEvent, option: SelectOption<string>): void {
    if (this.isMultiSelect) {
      event.stopPropagation(); // A click normally closes the menu, so prevent propagation to keep the menu open for more selections.

      this.selectionModel.toggle(option.value);
    } else {
      this.addFilter(option.value);
    }
  }

  public onClosedOptionsMenu(): void {
    if (!this.isMultiSelect || !this.hasSelectionModelChanged()) return;

    const filterValue: string = this.selectionModel.selected.map(encodeURIComponent).join(',');

    if (filterValue) {
      this.addFilter(filterValue);
    } else {
      this.removeFilter();
    }
  }

  private readonly store: Store = inject(Store);
  private readonly titleCasePipe: TitleCasePipe = inject(TitleCasePipe);
  private readonly selectionModel: SelectionModel<string> = new SelectionModel(true);
  private previousJoinedSelectionModelValue = '';

  private setFilterOptionsFor(definition: TableCardHeaderFilterDefinition): void {
    if (!isSelectFilterDefinition(definition)) {
      this.filterOptionsSelector = undefined;
      return;
    }

    this.filterOptionsSelector =
      isArray(definition.filterValues)
        ? createSelector( // Make the directly-defined filter values mimic a selector so the template doesn't have to deal with multiple option formats.
          () => (definition.filterValues as string[]).map<ListFilterSelectOption>(filterValue => ({
            displayName: this.titleCasePipe.transform(filterValue),
            value: filterValue,
            count: undefined
          }))
        )
        : definition.filterValues;
  }

  private initializeSelectionModel(): void {
    if (!this.isMultiSelect || isUndefined(this.filterOptionsSelector)) return;

    const filterOptionsAsSelectOptionsSelector: MxSelector<SelectOptions<string>> = createSelector(
      this.filterOptionsSelector,
      options => options.map(({displayName, value}) => ({displayName, value}))
    );

    this.store.select(this.routerSelectors.queryParamValueAsSelectOptions(this.queryParamName, filterOptionsAsSelectOptionsSelector))
      .pipe(
        filter<SelectOptions<string>>(isPopulatedArray),
        take(1)
      )
      .subscribe(options => // eslint-disable-line rxjs-angular/prefer-takeuntil -- This observable will complete on its own thanks to take(1).
        this.selectionModel.setSelection(...options.map(option => option.value))
      );
  }

  private hasSelectionModelChanged(): boolean {
    const currentJoinedSelectionModelValue = [...this.selectionModel.selected].sort((a, b) => a.localeCompare(b)).map(encodeURIComponent).join(',');

    if (currentJoinedSelectionModelValue === this.previousJoinedSelectionModelValue) return false;

    this.previousJoinedSelectionModelValue = currentJoinedSelectionModelValue;

    return true;
  }

  private addFilter(value: string): void {
    this.store.dispatch(RouterActions.addFilterParam(this.queryParamName, value));
  }

  private removeFilter(): void {
    this.store.dispatch(RouterActions.removeFilterParam(this.queryParamName));
  }
}
